// src/pages/dashboard/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  LinearProgress,
  Tooltip,
  Badge,
  Avatar,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  People as PeopleIcon,
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  WarningAmber as WarningAmberIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  DoDisturb as DoDisturbIcon,
  Autorenew as AutorenewIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Print as PrintIcon,
  WhatsApp as WhatsAppIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  HourglassEmpty as HourglassIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';

// GraphQL Queries and Mutations
import { GET_DASHBOARD_STATS } from '../../graphql/dashboard.graphql';
import { 
  GET_TEMPORARY_REGISTRATIONS,
  GET_EXPIRED_TEMPORARY_REGISTRATIONS,
  GET_TEMPORARY_REGISTRATION_STATS,
  SEND_REMINDER_NOTIFICATION,
  DELETE_TEMPORARY_REGISTRATION,
  CONVERT_TO_PERMANENT_REGISTRATION,
  CLEANUP_EXPIRED_REGISTRATIONS,
} from '../../graphql/temporary-registration.graphql';
import { GET_RECENT_REGISTRATIONS } from '../../graphql/registration.graphql';

// Types
import { TemporaryRegistration, RegistrationStats } from '../../types/temporary-registration.types';
import { Registration } from '../../types/registration.types';
import { useAuth } from '../../context/AuthContext';

// Define response types for mutations
interface SendReminderResponse {
  sendReminderNotification: {
    success: boolean;
    message: string;
    sentVia: string;
    error?: string;
  };
}

interface DeleteRegistrationResponse {
  deleteTemporaryRegistration: {
    success: boolean;
    message: string;
    error?: string;
  };
}

interface ConvertToPermanentResponse {
  convertToPermanentRegistration: {
    success: boolean;
    message: string;
    registrationId: number;
    registrationNo: string;
    error?: string;
  };
}

interface CleanupExpiredResponse {
  cleanupExpiredRegistrations: {
    success: boolean;
    message: string;
    deletedCount: number;
    error?: string;
  };
}

// Styled Components
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(() => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: '16px',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 20px 30px -10px rgba(102, 126, 234, 0.5)',
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Define response types for queries
interface DashboardStatsResponse {
  getDashboardStats: {
    todayPatients: number;
    pendingTests: number;
    todayRevenue: number;
    reportsPending: number;
  };
}

interface TempRegistrationsResponse {
  getTemporaryRegistrations: {
    success: boolean;
    data?: TemporaryRegistration[];
    totalCount?: number;
  };
}

interface TempStatsResponse {
  getTemporaryRegistrationStats: RegistrationStats;
}

interface ExpiredTempResponse {
  getExpiredTemporaryRegistrations: TemporaryRegistration[];
}

interface RecentRegResponse {
  getRecentRegistrations: Registration[];
}

export const Dashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
 const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRegistration, setSelectedRegistration] = useState<TemporaryRegistration | null>(null);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [reminderType, setReminderType] = useState<'sms' | 'email' | 'whatsapp'>('sms');
  const [autoRefresh, setAutoRefresh] = useState(true);


  useEffect(() => {
  // Check authentication on mount and on storage events (for multi-tab support)
  const checkAuth = () => {
    if (!isAuthenticated) {
      navigate('/employee-login');
    }
  };

  checkAuth();

  // Listen for storage events (if user logs out in another tab)
  window.addEventListener('storage', checkAuth);
  
  return () => {
    window.removeEventListener('storage', checkAuth);
  };
}, [isAuthenticated, navigate]);

  // GraphQL Queries
  const { data: statsData, refetch: refetchStats } = useQuery<DashboardStatsResponse>(GET_DASHBOARD_STATS, {
    pollInterval: autoRefresh ? 30000 : 0,
  });

  const { data: tempStatsData, refetch: refetchTempStats } = useQuery<TempStatsResponse>(GET_TEMPORARY_REGISTRATION_STATS);

  const { data: tempRegData, loading: tempRegLoading, refetch: refetchTempReg } = useQuery<TempRegistrationsResponse>(
    GET_TEMPORARY_REGISTRATIONS, 
    {
      variables: {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page: 1,
        limit: 20,
      },
    }
  );

  const { data: expiredTempData, refetch: refetchExpired } = useQuery<ExpiredTempResponse>(GET_EXPIRED_TEMPORARY_REGISTRATIONS);

  const { data: recentRegData, refetch: refetchRecent } = useQuery<RecentRegResponse>(GET_RECENT_REGISTRATIONS, {
    variables: { limit: 10 },
  });

  // Mutations with proper typing
  const [sendReminder] = useMutation<SendReminderResponse>(SEND_REMINDER_NOTIFICATION);
  const [deleteTemporaryRegistration] = useMutation<DeleteRegistrationResponse>(DELETE_TEMPORARY_REGISTRATION);
  const [convertToPermanent] = useMutation<ConvertToPermanentResponse>(CONVERT_TO_PERMANENT_REGISTRATION);
  const [cleanupExpired] = useMutation<CleanupExpiredResponse>(CLEANUP_EXPIRED_REGISTRATIONS);

  // Filter temporary registrations
  const filteredTempRegistrations = tempRegData?.getTemporaryRegistrations?.data?.filter((reg: TemporaryRegistration) => {
    const matchesSearch = searchTerm === '' || 
      reg.patientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.registrationNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.mobileNumber?.includes(searchTerm);
    return matchesSearch;
  }) || [];

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, registration: TemporaryRegistration) => {
    setAnchorEl(event.currentTarget);
    setSelectedRegistration(registration);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle continue registration
  const handleContinueRegistration = (registration: TemporaryRegistration) => {
    navigate('/registration', {
      state: {
        tempRegistrationId: registration.id,
        autoFill: true,
      },
    });
  };

  // Handle send reminder
  const handleSendReminder = async () => {
    if (!selectedRegistration) return;

    try {
      const { data } = await sendReminder({
        variables: { id: selectedRegistration.id },
      });

      if (data?.sendReminderNotification?.success) {
        enqueueSnackbar(`Reminder sent via ${data.sendReminderNotification.sentVia}`, { variant: 'success' });
        setShowReminderDialog(false);
      } else {
        enqueueSnackbar(data?.sendReminderNotification?.error || 'Failed to send reminder', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      enqueueSnackbar('Failed to send reminder', { variant: 'error' });
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedRegistration) return;

    try {
      const { data } = await deleteTemporaryRegistration({
        variables: { id: selectedRegistration.id },
      });

      if (data?.deleteTemporaryRegistration?.success) {
        enqueueSnackbar('Registration deleted successfully', { variant: 'success' });
        refetchTempReg();
        refetchExpired();
        refetchTempStats();
        setShowDeleteDialog(false);
      }
    } catch (error) {
      console.error('Error deleting registration:', error);
      enqueueSnackbar('Failed to delete registration', { variant: 'error' });
    }
  };

  // Handle convert to permanent
  const handleConvertToPermanent = async (id: number) => {
    try {
      const { data } = await convertToPermanent({
        variables: { id },
      });

      if (data?.convertToPermanentRegistration?.success) {
        enqueueSnackbar('Registration converted to permanent successfully', { variant: 'success' });
        refetchTempReg();
        refetchExpired();
        refetchRecent();
        refetchStats();
        refetchTempStats();
        
        navigate('/quick-registration', {
          state: {
            registrationNo: data.convertToPermanentRegistration.registrationNo,
            tempRegistrationId: id,
          },
        });
      }
    } catch (error) {
      console.error('Error converting to permanent:', error);
      enqueueSnackbar('Failed to convert registration', { variant: 'error' });
    }
  };

  // Handle cleanup expired
  const handleCleanupExpired = async () => {
    try {
      const { data } = await cleanupExpired();

      if (data?.cleanupExpiredRegistrations?.success) {
        enqueueSnackbar(data.cleanupExpiredRegistrations.message, { variant: 'success' });
        refetchExpired();
        refetchTempReg();
        refetchTempStats();
      }
    } catch (error) {
      console.error('Error cleaning up expired:', error);
      enqueueSnackbar('Failed to clean up expired registrations', { variant: 'error' });
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    refetchStats();
    refetchTempStats();
    refetchTempReg();
    refetchExpired();
    refetchRecent();
    enqueueSnackbar('Dashboard refreshed', { variant: 'success' });
  };

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        refetchStats();
        refetchTempStats();
        refetchTempReg();
        refetchExpired();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refetchStats, refetchTempStats, refetchTempReg, refetchExpired]);

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'warning';
      case 'COMPLETED':
        return 'success';
      case 'EXPIRED':
        return 'error';
      case 'CONVERTED':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <HourglassIcon />;
      case 'COMPLETED':
        return <CheckCircleOutlineIcon />;
      case 'EXPIRED':
        return <DoDisturbIcon />;
      case 'CONVERTED':
        return <AutorenewIcon />;
      default:
        return <AccessTimeIcon />;
    }
  };

  // Calculate time remaining
  const getTimeRemaining = (expiresAt?: string) => {
    if (!expiresAt) return 'N/A';
    try {
      return formatDistanceToNow(new Date(expiresAt), { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        flexDirection={isMobile ? 'column' : 'row'}
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Dashboard
          </Typography>
          <Chip 
            icon={autoRefresh ? <AutorenewIcon /> : <AccessTimeIcon />}
            label={autoRefresh ? 'Auto-refresh On' : 'Auto-refresh Off'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            color={autoRefresh ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={() => setShowStatsDialog(true)}
          >
            Analytics
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/registration')}
          >
            New Registration
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Today's Patients */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StyledCard>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="rgba(255,255,255,0.7)" gutterBottom variant="body2">
                    Today's Patients
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {statsData?.getDashboardStats?.todayPatients || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <PeopleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Pending Tests */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #dc004e 0%, #9a0036 100%)',
            color: 'white',
            borderRadius: '16px',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="rgba(255,255,255,0.7)" gutterBottom variant="body2">
                    Pending Tests
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {statsData?.getDashboardStats?.pendingTests || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <ScienceIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Reports Pending */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            color: 'white',
            borderRadius: '16px',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="rgba(255,255,255,0.7)" gutterBottom variant="body2">
                    Reports Pending
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {statsData?.getDashboardStats?.reportsPending || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <AssignmentIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Active Drafts */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: '#1a1a2e',
            borderRadius: '16px',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="rgba(26,26,46,0.7)" gutterBottom variant="body2">
                    Active Drafts
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {tempStatsData?.getTemporaryRegistrationStats?.totalDraft || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(26,26,46,0.2)', width: 56, height: 56 }}>
                  <HourglassIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts Section */}
      <AnimatePresence>
        {expiredTempData?.getExpiredTemporaryRegistrations &&
         expiredTempData.getExpiredTemporaryRegistrations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Paper sx={{ 
              p: 2, 
              mb: 3, 
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'warning.main',
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
            }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <WarningAmberIcon color="warning" />
                  <Box>
                    <Typography variant="h6" color="warning.dark">
                      {expiredTempData.getExpiredTemporaryRegistrations.length} Expired Registration(s)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      These registrations have expired and need attention
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<DeleteIcon />}
                  onClick={handleCleanupExpired}
                >
                  Clean Up Expired
                </Button>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <Paper sx={{ width: '100%', mb: 2, borderRadius: '12px' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : undefined}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: isMobile ? '0.8rem' : '1rem',
              fontWeight: 600,
            },
          }}
        >
          <Tab icon={<AccessTimeIcon />} label="Temporary Registrations" iconPosition="start" />
          <Tab icon={<CheckCircleIcon />} label="Completed Registrations" iconPosition="start" />
          <Tab icon={<AssessmentIcon />} label="Analytics" iconPosition="start" />
        </Tabs>

        {/* Temporary Registrations Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search by name, ID, or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 2, minWidth: '200px' }}
            />
            <FormControl sx={{ minWidth: '150px' }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="EXPIRED">Expired</MenuItem>
                <MenuItem value="CONVERTED">Converted</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {tempRegLoading ? (
            <LinearProgress />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>ID</TableCell>
                    <TableCell>Patient Name</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tests</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTempRegistrations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          No temporary registrations found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTempRegistrations.map((reg: TemporaryRegistration) => (
                      <TableRow key={reg.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {reg.registrationNo}
                          </Typography>
                        </TableCell>
                        <TableCell>{reg.patientName || 'N/A'}</TableCell>
                        <TableCell>{reg.mobileNumber || 'N/A'}</TableCell>
                        <TableCell>
                          <Tooltip title={new Date(reg.createdAt).toLocaleString()}>
                            <Typography variant="body2">
                              {formatDistanceToNow(new Date(reg.createdAt), { addSuffix: true })}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={<AccessTimeIcon />}
                            label={getTimeRemaining(reg.expiresAt)}
                            color={reg.expiresAt && new Date(reg.expiresAt) < new Date() ? 'error' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={getStatusIcon(reg.status)}
                            label={reg.status}
                            color={getStatusColor(reg.status) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Badge badgeContent={reg.selectedTests?.length || 0} color="primary">
                            <ScienceIcon color="action" />
                          </Badge>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" justifyContent="center" gap={1}>
                            <Tooltip title="Continue Registration">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleContinueRegistration(reg)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Send Reminder">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => {
                                  setSelectedRegistration(reg);
                                  setShowReminderDialog(true);
                                }}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Convert to Permanent">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleConvertToPermanent(reg.id)}
                                disabled={reg.status !== 'DRAFT'}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="More Actions">
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, reg)}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Completed Registrations Tab */}
        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Reg No.</TableCell>
                  <TableCell>Patient Name</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Tests</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentRegData?.getRecentRegistrations?.map((reg: Registration) => (
                  <TableRow key={reg.RegistrationId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {reg.RegistrationNo}
                      </Typography>
                    </TableCell>
                    <TableCell>{reg.CustomerName}</TableCell>
                    <TableCell>{reg.MobileNumber}</TableCell>
                    <TableCell>{new Date(reg.CreatedOn).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Badge badgeContent={reg.tests?.length || 0} color="primary">
                        <ScienceIcon color="action" />
                      </Badge>
                    </TableCell>
                    <TableCell>₹{reg.TotalAmount}</TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton size="small" color="primary">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print Receipt">
                          <IconButton size="small" color="secondary">
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Send WhatsApp">
                          <IconButton size="small" color="success">
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Registration Statistics
                  </Typography>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Total Drafts:</Typography>
                      <Typography fontWeight={600}>{tempStatsData?.getTemporaryRegistrationStats?.totalDraft || 0}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Completed:</Typography>
                      <Typography fontWeight={600}>{tempStatsData?.getTemporaryRegistrationStats?.totalCompleted || 0}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Expired:</Typography>
                      <Typography fontWeight={600}>{tempStatsData?.getTemporaryRegistrationStats?.totalExpired || 0}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Converted:</Typography>
                      <Typography fontWeight={600}>{tempStatsData?.getTemporaryRegistrationStats?.totalConverted || 0}</Typography>
                    </Box>
                    <Divider />
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Conversion Rate:</Typography>
                      <Chip 
                        label={`${tempStatsData?.getTemporaryRegistrationStats?.conversionRate || 0}%`}
                        color="success"
                        size="small"
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Popular Tests
                  </Typography>
                  <Stack spacing={2}>
                    {tempStatsData?.getTemporaryRegistrationStats?.popularTests?.map((test: any, index: number) => (
                      <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography>{test.testName}</Typography>
                        <Chip label={`${test.count} times`} size="small" />
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* More Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedRegistration) {
            navigator.clipboard.writeText(selectedRegistration.registrationNo || '');
            enqueueSnackbar('Registration number copied', { variant: 'success' });
          }
          handleMenuClose();
        }}>
          <AssignmentIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography>Copy ID</Typography>
        </MenuItem>
        <MenuItem onClick={() => {
          setShowDeleteDialog(true);
          handleMenuClose();
        }}>
          <DeleteIcon fontSize="small" color="error" sx={{ mr: 1 }} />
          <Typography color="error">Delete</Typography>
        </MenuItem>
      </Menu>

      {/* Send Reminder Dialog */}
      <Dialog open={showReminderDialog} onClose={() => setShowReminderDialog(false)}>
        <DialogTitle>Send Reminder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Send a reminder to {selectedRegistration?.patientName || 'the patient'} about their incomplete registration.
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Reminder Type</InputLabel>
            <Select
              value={reminderType}
              label="Reminder Type"
              onChange={(e) => setReminderType(e.target.value as any)}
            >
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReminderDialog(false)}>Cancel</Button>
          <Button onClick={handleSendReminder} variant="contained" color="primary">
            Send Reminder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this temporary registration?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stats Dialog */}
      <Dialog 
        open={showStatsDialog} 
        onClose={() => setShowStatsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AssessmentIcon />
            Detailed Analytics
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h3" color="primary">
                  {tempStatsData?.getTemporaryRegistrationStats?.totalDraft || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Drafts
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h3" color="success.main">
                  {tempStatsData?.getTemporaryRegistrationStats?.totalConverted || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Conversions
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStatsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};


// import React from 'react';
// import {
//   Container,
//   Grid,
//   Paper,
//   Typography,
//   Box,
//   Card,
//   CardContent,
//   Button,
// } from '@mui/material';
// import {
//   People as PeopleIcon,
//   Science as ScienceIcon,
//   Payment as PaymentIcon,
//   Assignment as AssignmentIcon,
// } from '@mui/icons-material';
// import { useNavigate } from 'react-router-dom';

// export const Dashboard: React.FC = () => {
//   const navigate = useNavigate();

//   const stats = [
//     { title: 'Today\'s Patients', value: '47', icon: <PeopleIcon />, color: '#1976d2' },
//     { title: 'Pending Tests', value: '23', icon: <ScienceIcon />, color: '#dc004e' },
//     { title: 'Today\'s Revenue', value: '₹45,000', icon: <PaymentIcon />, color: '#4caf50' },
//     { title: 'Reports Pending', value: '12', icon: <AssignmentIcon />, color: '#ff9800' },
//   ];

//   return (
//     <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
//       <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
//         <Typography variant="h4">Dashboard</Typography>
//         <Button 
//           variant="contained" 
//           onClick={() => navigate('/registration')}
//         >
//           New Registration
//         </Button>
//       </Box>

//       <Grid container spacing={3}>
//         {stats.map((stat, index) => (
//           <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
//             <Card>
//               <CardContent>
//                 <Box display="flex" alignItems="center" justifyContent="space-between">
//                   <Box>
//                     <Typography color="textSecondary" gutterBottom variant="body2">
//                       {stat.title}
//                     </Typography>
//                     <Typography variant="h5">{stat.value}</Typography>
//                   </Box>
//                   <Box sx={{ 
//                     backgroundColor: stat.color, 
//                     borderRadius: '50%', 
//                     p: 1,
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                   }}>
//                     {React.cloneElement(stat.icon, { sx: { color: 'white' } })}
//                   </Box>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}

//         <Grid  size={{ xs: 12 }}>
//           <Paper sx={{ p: 2 }}>
//             <Typography variant="h6" gutterBottom>
//               Recent Registrations
//             </Typography>
//             {/* Add table here */}
//           </Paper>
//         </Grid>
//       </Grid>
//     </Container>
//   );
// };